import {type FormEvent, useMemo, useState} from "react";
import {BaseAlert, BaseButton, BaseCard, BaseInput, BaseSelect, type SelectOption} from "@/components";
import {usePagoMovilAccounts} from "@/pages/private/PagoMovilAccounts/usePagoMovilAccounts";
import {useWallet} from "@/pages/private/Wallet/useWallet";
import {useWithdrawalPagoMovil} from "./useWithdrawalPagoMovil";
import {type WithdrawalFormInput, withdrawalSchema} from "./withdrawal.schema";

type FieldErrors = Partial<Record<keyof WithdrawalFormInput, string>>;

const INITIAL_FORM: WithdrawalFormInput = {
  pm_account_id: "",
  amount: "" as unknown as number,
};

export interface WithdrawalPagoMovilPageProps {
  onBack?: () => void;
  onSuccess?: () => void;
  onManageAccounts?: () => void;
}

const WithdrawalPagoMovilPage = ({onBack, onSuccess, onManageAccounts}: WithdrawalPagoMovilPageProps) => {
  const {accounts, isLoading: isLoadingAccounts} = usePagoMovilAccounts();
  const {wallet} = useWallet();
  const mutation = useWithdrawalPagoMovil();

  const [form, setForm] = useState<WithdrawalFormInput>(INITIAL_FORM);
  const [errors, setErrors] = useState<FieldErrors>({});

  const accountOptions = useMemo<SelectOption<string>[]>(
    () =>
      accounts.map(acc => ({
        value: acc.id,
        label: `${acc.bankName} · ${acc.phoneNumber}`,
      })),
    [accounts]
  );

  const availableBalance = wallet?.defaultAccount?.balance ?? 0;
  const currency = wallet?.defaultAccount?.currency ?? "VES";

  const updateField = <K extends keyof WithdrawalFormInput>(key: K, value: WithdrawalFormInput[K]) => {
    setForm(prev => ({...prev, [key]: value}));
    setErrors(prev => ({...prev, [key]: undefined}));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsed = withdrawalSchema.safeParse(form);
    if (!parsed.success) {
      const fieldErrors: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof WithdrawalFormInput | undefined;
        if (key && !fieldErrors[key]) fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }
    if (parsed.data.amount > availableBalance) {
      setErrors({amount: "Monto superior a tu saldo disponible"});
      return;
    }
    mutation.mutate(parsed.data);
  };

  const handleReset = () => {
    setForm(INITIAL_FORM);
    setErrors({});
    mutation.reset();
  };

  if (mutation.isSuccess) {
    return (
      <section className="mx-auto flex w-full max-w-lg flex-col gap-4 px-4 py-8">
        <BaseAlert
          variant="success"
          title="Retiro solicitado"
        >
          <p className="mb-3">Tu solicitud fue registrada. Procesaremos el pago a tu cuenta Pago Móvil.</p>
          <div className="flex flex-wrap gap-2">
            <BaseButton
              variant="primary"
              size="sm"
              onClick={handleReset}
            >
              Nuevo retiro
            </BaseButton>
            {onSuccess ? (
              <BaseButton
                variant="secondary"
                size="sm"
                onClick={onSuccess}
              >
                Volver a la billetera
              </BaseButton>
            ) : null}
          </div>
        </BaseAlert>
      </section>
    );
  }

  if (!isLoadingAccounts && accounts.length === 0) {
    return (
      <section className="mx-auto flex w-full max-w-lg flex-col gap-4 px-4 py-8">
        <BaseAlert
          variant="info"
          title="Agrega una cuenta Pago Móvil"
        >
          <p className="mb-3">Necesitas registrar al menos una cuenta Pago Móvil antes de solicitar retiros.</p>
          {onManageAccounts ? (
            <BaseButton
              variant="primary"
              size="sm"
              onClick={onManageAccounts}
            >
              Gestionar cuentas
            </BaseButton>
          ) : null}
        </BaseAlert>
      </section>
    );
  }

  return (
    <section className="mx-auto flex w-full max-w-lg flex-col gap-6 px-4 py-8">
      <header className="flex flex-col gap-2">
        <h1 className="text-[var(--font-h1)] font-bold text-[var(--color-text)]">Retiro Pago Móvil</h1>
        <p className="text-[var(--font-body-lg)] text-[var(--color-text-secondary)]">
          Saldo disponible:{" "}
          <span className="font-semibold text-[var(--color-text)]">
            {availableBalance.toFixed(2)} {currency}
          </span>
        </p>
      </header>

      {mutation.isError ? (
        <BaseAlert
          variant="danger"
          title="No pudimos procesar tu retiro"
        >
          <p>{mutation.error?.message ?? "Inténtalo nuevamente."}</p>
        </BaseAlert>
      ) : null}

      <BaseCard
        variant="surface"
        padding="lg"
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={handleSubmit}
          noValidate
        >
          <BaseSelect
            label="Cuenta destino"
            placeholder={isLoadingAccounts ? "Cargando cuentas..." : "Selecciona una cuenta"}
            options={accountOptions}
            value={form.pm_account_id}
            onChange={e => updateField("pm_account_id", e.target.value)}
            error={errors.pm_account_id}
            disabled={mutation.isPending || isLoadingAccounts}
          />

          <BaseInput
            label={`Monto (${currency})`}
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            placeholder="0.00"
            value={String(form.amount ?? "")}
            onChange={e => updateField("amount", e.target.value as unknown as number)}
            error={errors.amount}
            disabled={mutation.isPending}
          />

          <div className="mt-2 flex flex-col gap-2 sm:flex-row">
            <BaseButton
              type="submit"
              variant="primary"
              disabled={mutation.isPending}
              fullWidth
            >
              {mutation.isPending ? "Enviando..." : "Solicitar retiro"}
            </BaseButton>
            {onBack ? (
              <BaseButton
                type="button"
                variant="ghost"
                onClick={onBack}
                disabled={mutation.isPending}
              >
                Cancelar
              </BaseButton>
            ) : null}
          </div>
        </form>
      </BaseCard>
    </section>
  );
};

export default WithdrawalPagoMovilPage;
